import { IdentityProviderService } from './identity-provider.service';
import { TriggerbeeApi, TriggerbeeRequester } from './../triggerbee/triggerbee-requester.service';
import { UserIdentity } from './user-identity';
import { Logger, LoggerFactory } from './../logger.service';
import TimeUtils from '../../shared/utils/time-utils';
import { Subscription } from 'rxjs';
import { EventEmitter, Injectable, OnDestroy, Output } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { HttpRequestDecorator } from '../triggerbee/http.request.decorator';
import { CustomersService } from 'app/customers/customers.service';
import { StorageCurrentContextService } from '../storage-current-context.service';
import { CurrentSiteIdStore } from '../current-site-id.store';

// Cross-tab channel for broadcasting session changes (logout, identity updates).
const SESSION_CHANNEL_NAME = 'triggerbee-session';

type SessionChannelMessage = { type: 'signout'; reason: string } | { type: 'identity-changed' };

export { UserIdentity, Section, SectionRole, SiteAccess, SectionAccess, GlobalRole, Language } from './user-identity';

@Injectable()
export class IdentityService implements OnDestroy {
  private readonly logger: Logger;
  private subject: Subscription;
  private sessionChannel: BroadcastChannel | undefined;
  private storageListener: ((event: StorageEvent) => void) | undefined;
  private fallbackPollHandle: ReturnType<typeof setInterval> | undefined;
  @Output() onSignout = new EventEmitter();

  // Gets the current identity. Do not bind to this on views directly, subscribe to onIdentityChanged instead.
  public get currentIdentity() {
    return this.identityProvider.currentIdentity;
  }

  public onIdentityChanged = new EventEmitter<UserIdentity>(false);
  public currentCustomerSubscription: Subscription;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly currentSiteIdStore: CurrentSiteIdStore,
    private readonly router: Router,
    private readonly identityProvider: IdentityProviderService,
    private readonly requester: TriggerbeeRequester,
    private http: HttpRequestDecorator,
    private readonly customersService: CustomersService,
    private readonly storageCurrentContextService: StorageCurrentContextService,
  ) {
    this.logger = loggerFactory.get('IdentityService');

    // Identity provider does the lower level dirtiness change. It detects changes lazily when and
    // fires onIdentityChanged if the identity is dirty.
    this.scheduleIdentityUpdates();
  }

  get hasCurrentIdentity(): boolean {
    return this.currentIdentity !== undefined;
  }

  // User is verified if the isVerified flag is set or in case is not verified check if user is created during the last 24 hours
  get isUserVerified(): boolean {
    if (this.hasCurrentIdentity) {
      if (this.currentIdentity.isVerified) {
        return true;
      } else if (!this.currentIdentity.createdAt) {
        // TODO: Every identity should have date created in backend, but for the moment the old ones don't
        this.logger.debug('User has no createdAt in backend');
        return false;
      } else {
        this.logger.debug(
          'User is not verified and will be checked it if was created during last 24 hours. -> ' +
            TimeUtils.isTimestampInLastXHours(this.currentIdentity.createdAt * 1000, 24),
        );
        // True if created in later than 24 hours ago
        return TimeUtils.isTimestampInLastXHours(this.currentIdentity.createdAt * 1000, 24);
      }
    }
    return false;
  }

  // Makes server request to update identity payload.
  // signOutOnFailure: when true (default), a failed JWT refresh signs the user out and
  // redirects to login. Pass false from flows where the caller has just established a
  // valid session (e.g. completing registration) and a stale JWT must not abort the flow.
  public async requestUpdateIdentityPayloadAsync(signOutOnFailure: boolean = true): Promise<boolean> {
    if (!this.currentIdentity) {
      this.logger.error('Cannot update identity when there is none.');
      return false;
    }
    const response = await this.requester.postAsync<string>(
      TriggerbeeApi.Gateway,
      '/authentication/jwt/update-payload',
    );
    if (!response.isPositive()) {
      this.logger.error(`Update JWT failed. Response from identity was ${response.toString()}`);
      if (signOutOnFailure) {
        this.signOut('jwt-update-failed', true);
      }
      return false;
    }
    return true;
  }

  public signOut(reason: string = 'unknown', redirect: boolean = true): void {
    const callbackUrl = this.getCallbackUrl();

    if (!this.currentIdentity) {
      this.logger.error(`Sign-out requested when user is not signed in (reason=${reason})`);
      return;
    }
    this.logger.info(`Sign-out requested (reason=${reason}). Stack: ${new Error().stack}`);
    this.identityProvider.clearIdentity();
    this.requester.postAsync(TriggerbeeApi.Gateway, '/authentication/logout').catch(() => {});
    this.currentSiteIdStore.clear();
    this.onIdentityChanged.emit(undefined);
    this.onSignout.emit();
    this.customersService.clearCurrentCustomerCache();
    this.storageCurrentContextService.remove();

    this.broadcastSignOut(reason);

    // Redirect
    if (redirect) {
      let extras: NavigationExtras = undefined;
      if (callbackUrl) {
        extras = { queryParams: { callbackUrl: callbackUrl } };
      }
      this.router.navigate(['account/login'], extras);
    }
  }

  public notifyPossibleIdentityUpdate() {
    // A call to identity provider do the dirtiness check and fires the event chain.
    this.identityProvider.notifyPossibleIdentityUpdate();
  }

  public async refreshIdentityAsync(): Promise<void> {
    await this.identityProvider.loadInitialIdentityAsync();
  }

  private getCallbackUrl(): string {
    // Get callback url
    let callbackUrl = this.router.routerState.snapshot.root.queryParams['callbackUrl'];
    if (!callbackUrl) {
      const currentUrl = this.router.url;
      if (!(currentUrl.startsWith('/account/login') || currentUrl.startsWith('/account/register'))) {
        // No need to have login / register urls as callback
        callbackUrl = currentUrl;
      }
    }
    return callbackUrl;
  }

  private scheduleIdentityUpdates() {
    // Subscribe to lower level change detection
    this.subject = this.identityProvider.onIdentityChanged.subscribe((identity: UserIdentity) => {
      // Inform the higher level observers
      this.onIdentityChanged.emit(identity);
      // Log the new identity
      this.logger.debug('Identity has been changed', identity);
    });

    // Cross-tab session sync via BroadcastChannel + storage events, with a low-frequency
    // fallback poll for edge cases (dev-tools edits, browser sync, no BroadcastChannel).
    this.setupCrossTabSessionSync();
  }

  private setupCrossTabSessionSync() {
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.sessionChannel = new BroadcastChannel(SESSION_CHANNEL_NAME);
        this.sessionChannel.onmessage = (event: MessageEvent<SessionChannelMessage>) => {
          const msg = event.data;
          if (!msg) return;
          if (msg.type === 'signout') {
            this.logger.info(`Received cross-tab signout (reason=${msg.reason})`);
            this.notifyPossibleIdentityUpdate();
            if (this.currentIdentity) {
              // Sign out locally without re-broadcasting to avoid ping-pong.
              this.signOutLocal(`cross-tab:${msg.reason}`);
            }
          } else if (msg.type === 'identity-changed') {
            this.notifyPossibleIdentityUpdate();
          }
        };
      } catch (err) {
        this.logger.warn(`BroadcastChannel setup failed: ${err?.message ?? err}`);
      }
    } else {
      this.logger.warn('BroadcastChannel is not supported; falling back to storage events only');
    }

    // Fallback: storage events fire in other tabs even without BroadcastChannel support.
    this.storageListener = (event: StorageEvent) => {
      if (event.key === 'triggerbee-session-signout' && event.newValue) {
        this.notifyPossibleIdentityUpdate();
        if (this.currentIdentity) {
          this.signOutLocal('cross-tab:storage-event');
        }
      }
    };
    window.addEventListener('storage', this.storageListener);

    // Low-frequency fallback poll for cookies changed outside our app.
    this.fallbackPollHandle = setInterval(() => {
      this.notifyPossibleIdentityUpdate();
    }, 240_000);
  }

  private broadcastSignOut(reason: string) {
    try {
      this.sessionChannel?.postMessage({ type: 'signout', reason } satisfies SessionChannelMessage);
    } catch (err) {
      this.logger.warn(`Failed to broadcast signout: ${err?.message ?? err}`);
    }
    // Storage-event fallback: timestamped value ensures the event fires even if the key exists.
    try {
      localStorage.setItem('triggerbee-session-signout', `${reason}:${Date.now()}`);
      // Remove immediately — other tabs already received the event.
      localStorage.removeItem('triggerbee-session-signout');
    } catch {
      // localStorage can throw in private-mode / disabled scenarios; not fatal.
    }
  }

  // Local sign-out without re-broadcasting. Use signOut() to also notify other tabs.
  private signOutLocal(reason: string) {
    const callbackUrl = this.getCallbackUrl();
    if (!this.currentIdentity) {
      return;
    }
    this.logger.info(`Local sign-out (reason=${reason}). Stack: ${new Error().stack}`);
    this.identityProvider.clearIdentity();
    this.currentSiteIdStore.clear();
    this.onIdentityChanged.emit(undefined);
    this.onSignout.emit();
    this.customersService.clearCurrentCustomerCache();
    this.storageCurrentContextService.remove();
    let extras: NavigationExtras = undefined;
    if (callbackUrl) {
      extras = { queryParams: { callbackUrl: callbackUrl } };
    }
    this.router.navigate(['account/login'], extras);
  }

  ngOnDestroy(): void {
    if (this.fallbackPollHandle !== undefined) {
      clearInterval(this.fallbackPollHandle);
      this.fallbackPollHandle = undefined;
    }
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = undefined;
    }
    try {
      this.sessionChannel?.close();
    } catch {
      // ignore
    }
    this.sessionChannel = undefined;
    this.subject?.unsubscribe();
  }

  public async resendVerificationEmailAsync(email: string, callbackPath: string, language: string): Promise<boolean> {
    const response = await this.requester.postAsync(TriggerbeeApi.Gateway, '/users/send-verification', {
      email,
      callbackPath,
      language,
    });
    return response.isPositive();
  }

  public async resendUserInvitationFromAdmin(email: string): Promise<boolean> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    await this.http.post(`${baseUrl}/admin/users/${email}/reinvite-user`, null).toPromise();
    return true;
  }
}
